import { Document, HeadingLevel, Packer, Paragraph, Table, TableRow, TextRun } from "docx";
import { lightTheme } from "styles/theme";

import { downloadFile, sanitizeFilename } from "lib/utils";
import { exportDataToCsv } from "../../../lib/exportArrayToCsv";
import { BidNoBidResponse } from "../../../lib/hooks/api/bid/answers/useGetBidReportAnswers";
import { BidReport } from "../../../lib/hooks/api/bid/bid_reports/useBidReport";
import {
  buildDocxFooter,
  buildMetaRow,
  buildMetaTable,
  buildQaTable,
} from "../common_report_components/docxExportUtils";

const placeholderColor = lightTheme.colors.sysTextPlaceholder.replace("#", "");

export function exportBidReportAsCsv(
  answersData: BidNoBidResponse,
  bidName: string | undefined,
): void {
  const questionsAndAnswers = answersData.flatMap((item) =>
    item.questions.map((question) => ({
      question: question.title,
      answer: question.answer?.answer || null,
    })),
  );
  const csvData = [
    { question: "Report link", answer: window.location.href },
    { question: "Question", answer: "Answer" },
    ...questionsAndAnswers,
  ];
  const sanitisedFilename = `Bid_no_bid_report${"_" + sanitizeFilename(bidName || "")}.csv`;
  exportDataToCsv(sanitisedFilename, csvData, false);
}

export async function exportBidReportAsDocx(
  answersData: BidNoBidResponse,
  bidName: string | undefined,
  notice?: BidReport["notice"],
): Promise<void> {
  const metaRows: TableRow[] = [];

  if (notice?.name) {
    metaRows.push(
      buildMetaRow("Notice title", notice.name, `${window.location.origin}/notices/${notice.guid}`),
    );
  }
  const buyer = notice?.buyers?.[0];
  if (buyer?.name) {
    metaRows.push(
      buildMetaRow("Buyer", buyer.name, `${window.location.origin}/buyers/${buyer.guid}`),
    );
  }

  const children: (Paragraph | Table)[] = [
    new Paragraph({
      heading: HeadingLevel.HEADING_1,
      children: [new TextRun("Bid / no-bid report")],
      spacing: { before: 200, after: 200 },
    }),
  ];

  if (metaRows.length > 0) {
    children.push(buildMetaTable(metaRows));
  }

  children.push(
    new Paragraph({
      children: [
        new TextRun({
          text: "Review the information below to make your bid / no-bid decision in minutes, not days.",
          italics: true,
          color: placeholderColor,
        }),
      ],
      spacing: { before: 200, after: 200 },
    }),
  );

  for (const group of answersData) {
    children.push(
      new Paragraph({
        heading: HeadingLevel.HEADING_2,
        children: [new TextRun(group.groupName)],
        spacing: { before: 200, after: 200 },
      }),
    );
    children.push(buildQaTable(group));
  }

  const doc = new Document({
    sections: [
      {
        properties: {
          page: { margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 } },
        },
        footers: { default: buildDocxFooter() },
        children,
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  downloadFile(blob, `Bid_no_bid_report_${sanitizeFilename(bidName || "")}.docx`);
}
